import React, { useEffect, useLayoutEffect, useState, useContext, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import dynamic from 'next/dynamic';
import { useSourceTextRepresentationEnabled } from '@/components/admin/source-text-flag';
import { markdownRehypeSanitizePlugins } from '@/lib/markdown-rehype-plugins';
import {
  CheckIcon,
  DownloadSimpleIcon,
  CaretDoubleDownIcon,
  CaretDoubleUpIcon,
  XIcon,
  ListPlusIcon,
  FileCloudIcon,
  FunnelIcon,
  PlusIcon,
  BookmarksSimpleIcon,
  FileMagnifyingGlassIcon,
} from "@phosphor-icons/react";
import {
  getMediaType,
  recordStat,
  isRestrictedFileType,
  extractPathAfterBulk,
  parseCitationPageFromSourceLike,
  isMetaFile,
  isMetaBundleFilename,
  resolveTranscriptUrlFromSourceContext,
  resolveTranscriptUrlFromFileInfo,
  isTranscriptJsonLocation,
  resolvePreferredMediaDisplayForSource,
  getDispatchedFilePath,
  isArchiveMemberSearchablePath,
  isContainerLikeSource,
  isContainerSelfSource,
  trackEvent,
  resolveFullS3Url,
  resolveDisplayLookupUrl,
  resolveFileInfoMediaDisplayUrl,
  shouldFetchFileInfoBeforeDisplay,
  getFileInfoBodyFromServer,
  fileInfoFromProvenanceBody,
  isChatCitationSource,
  isInlineJsonCitationDisplayable,
} from '@/shared/functions';
import type { HierarchyItem } from '@/shared/cors-provenance-api';
import { Button } from '@tranquility-ai/theme-components/button';
import { HierarchyActionMenu, hasHierarchyMenu, markdownPdfPreviewItem } from './source-hierarchy-menu';
import { TpTooltip } from '@tranquility-ai/theme-components/tooltip';
import { MediaDisplay, MediaDisplayRef, fetchMboxData } from './ui/mediaDisplay';
import { EmailAttachmentsList } from './email-attachments-list';
import { MediaPreviewSlot } from './ui/media-preview-slot';
import {
  fetchEmailCitation,
  isEmlEmailCitation,
  isMboxEmailJsonUrl,
  resolveEmailFetchArgs,
  urlsLikelySameAttachment,
  urlLooksLikeEml,
} from '@/shared/email-citation';
import { resolveSourceForActions as resolveSourceForActionsFromCitation } from '@/shared/resolve-source-for-actions';
import { MetaSocialViewerButton } from './ui/metaPreviewViewer';
import { MboxAttachment, MediaSource, MediaType, FileSummary } from '@/shared/types';
import { Source } from '../shared/types';
import { Card, CardHeader, CardBody } from "@heroui/card";
import { FeaturesContext, FullScreenContext, StatsContext } from '@/app/contexts';
import { useCsamAccess } from '@/lib/csam-access';
import { citationForViewer, fileSummaryForViewer } from '@/lib/csam-sensitive-text';
import { useJsonMediaDisplayEnabled } from '@/hooks/useMediaPlayerTransition';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import { EventFromChat } from './answer-item';
import { Spinner } from '@heroui/spinner';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { inspectRequestForHierarchyFile } from '@/shared/cors-edge-lambda-api';
import type { InspectTarget } from '@/components/admin/SourceInspectorModal';
import { isDisplayableMediaType } from '@/shared/constants';
import { isPreviewTelemetryEligible, resolveSourceCardFallback } from '@/shared/preview-outcome';
import {
  extensionFromRef,
  openingIdentity,
  reportUnsupportedPreview,
  resolvePreviewIdentity,
} from '@/lib/file-preview-telemetry';
import { ANSWER_ITEM_VIDEO_CARD_HEIGHT_SLACK_PX } from '@/shared/media-preview-layout';
import { PATH_DISPLAY_WHITESPACE_CLASSNAME } from '@/shared/uiConstants';
import { getAnswerSourceCaptureScope } from './ui/media/mediaIdentityKeys';
import { CsamDetectionPill } from './ui/media/csam-detection-pill';

function isUsableMediaType(
  mediaType: MediaType | undefined,
  jsonMediaDisplayEnabled = false,
): boolean {
  return isDisplayableMediaType(mediaType, { jsonMediaDisplay: jsonMediaDisplayEnabled });
}

/**
 * True when the source can render without waiting on file-info: either a
 * displayable preferred URL, or (with ``jsonMediaDisplay``) inline JSON content.
 */
function sourceCanDisplayWithoutFileInfo(
  source: Source | undefined,
  jsonMediaDisplayEnabled = false,
): boolean {
  if (!source) return false;
  const preferred = resolvePreferredMediaDisplayForSource(source, null);
  if (
    !!preferred.mediaUrl &&
    !isArchiveMemberSearchablePath(preferred.mediaUrl) &&
    isUsableMediaType(preferred.mediaType, jsonMediaDisplayEnabled)
  ) {
    return true;
  }
  return isInlineJsonCitationDisplayable(
    source,
    preferred.mediaType,
    jsonMediaDisplayEnabled,
  );
}

/**
 * True when Favorites can accept this source: displayable URL or content-only JSON
 * (``jsonMediaDisplay`` on). Mirrors UnifiedSources ``isMediaSource`` gate.
 */
function sourceCanAddToFavorites(
  source: Source | undefined,
  jsonMediaDisplayEnabled = false,
): boolean {
  if (!source) return false;
  const preferred = resolvePreferredMediaDisplayForSource(
    source,
    source.fileInfo ?? null,
  );
  if (
    !!preferred.mediaUrl &&
    !isArchiveMemberSearchablePath(preferred.mediaUrl) &&
    isUsableMediaType(preferred.mediaType, jsonMediaDisplayEnabled)
  ) {
    return true;
  }
  return isInlineJsonCitationDisplayable(
    source,
    preferred.mediaType,
    jsonMediaDisplayEnabled,
  );
}

function parseSourceContextEmailIndex(
  sourceContext: Source['sourceContext'] | undefined,
): number | null {
  if (!sourceContext) return null;
  const raw = sourceContext.email_index ?? sourceContext.emailIndex;
  if (raw === undefined || raw === null || raw === '') return null;
  const parsed = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

type MboxFetchHandle = {
  sourceFilePath: string | undefined;
  emailIndex: number | null;
  abortController: AbortController;
};

function isActiveMboxFetch(
  current: MboxFetchHandle | null,
  sourceFilePath: string | undefined,
  emailIndex: number | null,
): boolean {
  return (
    current?.sourceFilePath === sourceFilePath && current?.emailIndex === emailIndex
  );
}

/** Stable identity for async source-info / mbox loads (avoids one-frame stale loading=false). */
function buildSourceLoadKey(
  selectedSourceIndex: number,
  source: Source | undefined,
): string {
  return [
    selectedSourceIndex,
    source?.sourceFilePath ?? '',
    source?.analysisFilePath ?? '',
    source?.previewFilePath ?? '',
    source?.dispatchedFilePath ?? '',
    source?.sourceContext?.dispatchedFilePath ?? '',
    source?.sourceContext?.dispatched_file_path ?? '',
    getDispatchedFilePath(source),
    source?.sourceContext?.email_index ?? '',
    source?.sourceContext?.emailIndex ?? '',
    // Content-moderation variant: the obfuscated and original views differ only by
    // this flag, so it must invalidate the cached file-info (its ``previewUrl`` is
    // the asset actually rendered) even when the card is not remounted.
    source?.bypassCm ? 'bypass_cm' : '',
  ].join('|');
}

/** Citation page only when explicitly present on the source (avoids inventing page 1). */
function citationPageIfSpecified(source: Source): number | undefined {
  const raw =
    source.page ??
    source.pageNumber ??
    source.sourceContext?.pageNumber;
  if (raw === undefined || raw === null || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  if (Number.isFinite(n) && n >= 1) return n;
  return undefined;
}

/**
 * Clone for addToTimeline: preserve audio/video start & end in sourceContext; for other types,
 * merge an explicit citation page without wiping existing context (unlike assigning only pageNumber).
 */
function buildTimelinePrimarySource(source: Source): Source {
  const mediaType = getMediaType(source, source.fileInfo);
  const isAudioVideo = mediaType === 'audio' || mediaType === 'video';
  const baseContext = { ...(source.sourceContext || {}) };
  const sourceContext = isAudioVideo
    ? baseContext
    : (() => {
        const pageNum = citationPageIfSpecified(source);
        return pageNum !== undefined ? { ...baseContext, pageNumber: pageNum } : baseContext;
      })();
  const topPage = !isAudioVideo ? citationPageIfSpecified(source) : undefined;
  return {
    ...source,
    sourceContext,
    ...(topPage !== undefined ? { pageNumber: topPage } : {}),
  };
}

function mediaContextForTimelineMediaRow(
  timelinePrimary: Source,
  rowMediaType: MediaType,
): Record<string, unknown> | undefined {
  if (rowMediaType === 'audio' || rowMediaType === 'video') {
    const ctx = timelinePrimary.sourceContext;
    if (!ctx) return undefined;
    if (ctx.startTime == null && ctx.endTime == null) return undefined;
    return { ...ctx };
  }
  const pageNum = citationPageIfSpecified(timelinePrimary);
  if (pageNum !== undefined) {
    return { ...(timelinePrimary.sourceContext || {}), pageNumber: pageNum };
  }
  const ctx = timelinePrimary.sourceContext;
  if (ctx?.pageNumber != null) return { ...ctx };
  return undefined;
}

/** Plain JSON of the citation object, including every field the answer carried. */
function citationForTelemetry(source: Source): unknown {
  return JSON.parse(JSON.stringify(source));
}

function isMboxTypeSource(
  source: Source | undefined,
  fileInfo: FileSummary | null,
): boolean {
  const sourceFileType = (
    fileInfo?.extension ||
    source?.fileInfo?.extension ||
    ''
  ).toLowerCase();
  if (sourceFileType === 'mbox' || sourceFileType === 'mbx') return true;
  const candidatePath =
    source?.sourceFilePath ||
    source?.sourceUrl ||
    source?.previewFilePath ||
    fileInfo?.fileUrl ||
    fileInfo?.previewUrl ||
    '';
  const extension = candidatePath
    .split(/[?#]/)[0]
    .split('.')
    .pop()
    ?.trim()
    .toLowerCase() || '';
  return extension === 'mbox' || extension === 'mbx';
}

/** US Letter width÷height for paginated documents (pdf / docx / pptx). */
const DOCUMENT_PAGE_ASPECT = 8.5 / 11;
/** Reserved for MediaDisplay toolbar so the media canvas itself stays AR-correct. */
const MEDIA_TOOLBAR_CHROME_PX = 48;

/**
 * Width÷height for SourceCard media. Paginated docs use Letter; anything without a
 * known intrinsic height (text, image/video before load, etc.) defaults to 1:1.
 */
function aspectRatioForSourceMedia(mediaType: MediaType | undefined | null): number {
  switch (mediaType) {
    case 'pdf':
    case 'docx':
    case 'pptx':
      return DOCUMENT_PAGE_ASPECT;
    default:
      return 1;
  }
}

function fitHeightForMediaWidth(
  width: number,
  mediaType: MediaType | undefined | null,
): number {
  if (!(width > 0)) return 0;
  const aspect = aspectRatioForSourceMedia(mediaType);
  return Math.max(1, Math.round(width / aspect) + MEDIA_TOOLBAR_CHROME_PX);
}

/** Video/audio grow with player + transcript — never lock a short AR box. */
function isContinuousMediaType(mediaType: MediaType | undefined | null): boolean {
  return mediaType === 'video' || mediaType === 'audio';
}

/**
 * Space from this container's top to the bottom of the nearest scrollport.
 * The preview column is content-sized, so its own bounding rect tracks content,
 * not available space — sizing a PDF/image viewer from that feeds a resize loop.
 * Measuring against the scrollport is stable regardless of the viewer's content.
 */
function availableScrollportHeight(el: HTMLElement): number | null {
  const marked = el.closest('[data-source-card-scrollport]') as HTMLElement | null;
  let ancestor: HTMLElement | null = marked;
  if (!ancestor) {
    ancestor = el.parentElement;
    while (ancestor) {
      const overflowY = getComputedStyle(ancestor).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') break;
      ancestor = ancestor.parentElement;
    }
  }
  if (!ancestor) return null;
  const computed = getComputedStyle(ancestor);
  const maxH = parseFloat(computed.maxHeight);
  const portHeight = Number.isFinite(maxH) && maxH > 0 ? maxH : ancestor.clientHeight;
  // Viewport-relative only — do not add scrollTop. Content offset would shrink
  // avail as the scrollport scrolls even when the preview stays fully visible.
  const elOffsetFromPortTop =
    el.getBoundingClientRect().top - ancestor.getBoundingClientRect().top;
  const avail = portHeight - elOffsetFromPortTop;
  return avail > 0 ? Math.floor(avail) : null;
}

interface SourceCardProps {
  sources: Source[];
  selectedSourceIndex: number;
  answerIndex: number;
  epochTimeSecs: number;
  handleDownload: (source: Source) => void;
  handleAddSourceToScope: (source: Source, index: number) => void;
  handleCopySourceForTimeline: (source: Source, index: number) => void;
  handleCopyToMediaShare?: (source: Source, index: number, onSuccess?: () => void) => void;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>> | undefined;
  fullScreenSource: Source | undefined;
  copiedSource: number;
  addedSource: number;
  closeSources: () => void;
  /** Omit on surfaces with no timeline (HelioTrace); the create-event actions are then hidden. */
  addToTimeline?: (event: EventFromChat) => void;
  caseId?: string | null;
  /** Optional node rendered directly before the filename in the card header. */
  headerPrefix?: React.ReactNode;
  /** When set, shows Obfuscated/Original switcher in the media viewer toolbar. */
  showObfuscatedPreview?: boolean;
  onToggleObfuscatedPreview?: (show: boolean) => void;
  /** Content-moderation detection type (e.g. ``'CSAM'``) so captures derive an obfuscated variant. */
  contentModerationType?: string;
  /** Override when file-info omits ``contentModeration.imageDescription``. */
  contentModerationImageDescription?: string | null;
  /** True while a findings fallback description request is in flight. */
  contentModerationImageDescriptionPending?: boolean;
  /** Size the media preview to the card's available area (evidence locker overlay). */
  fillPreviewContainer?: boolean;
  /** Chat-history turn key for the answer that owns this citation, when the card is opened from chat. */
  chatKey?: string;
}

// Loaded on first open, so the inspector stays out of the main chat bundle.
const SourceInspectorModal = dynamic(
  () => import('@/components/admin/SourceInspectorModal').then((m) => m.SourceInspectorModal),
  { ssr: false },
);

export const SourceCard: React.FC<SourceCardProps> = ({
  sources,
  selectedSourceIndex,
  answerIndex,
  epochTimeSecs,
  handleDownload,
  handleAddSourceToScope,
  handleCopySourceForTimeline,
  handleCopyToMediaShare,
  setIsExpanded,
  fullScreenSource,
  copiedSource,
  addedSource,
  closeSources,
  addToTimeline,
  caseId,
  headerPrefix,
  showObfuscatedPreview,
  onToggleObfuscatedPreview,
  contentModerationType,
  contentModerationImageDescription,
  contentModerationImageDescriptionPending = false,
  fillPreviewContainer = false,
  chatKey,
}) => {
  const t = useTimePilotT();
  // "What TimePilot sees" inspector, anywhere a source card renders, behind
  // the view-source-text-representation flag.
  const sourceTextEnabled = useSourceTextRepresentationEnabled();
  const [inspected, setInspected] = useState<{ source: Source; target: InspectTarget | null } | null>(null);
  const [createdEventFromSource, setCreatedEventFromSource] = useState(false);
  const [fileInfo, setFileInfo] = useState<FileSummary | null>(null);
  const [hierarchy, setHierarchy] = useState<HierarchyItem[] | null>(null);
  const [fileInfoLoadError, setFileInfoLoadError] = useState(false);
  const [loadedSourceLoadKey, setLoadedSourceLoadKey] = useState<string | null>(null);
  const [telemetryReadyKey, setTelemetryReadyKey] = useState<string | null>(null);
  const [isCitationExpanded, setIsCitationExpanded] = useState(false);
  const [attachmentUrl, setAttachmentUrl] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<MboxAttachment[] | null>(null);
  const [mboxEmailUrl, setMboxEmailUrl] = useState<string | null>(null);
  const [openAttachmentIndex, setOpenAttachmentIndex] = useState<number | null>(null);
  const [copiedAttachementIndex, setCopiedAttachementIndex] = useState<number | null>(null);
  const [createdEventFromAttachment, setCreatedEventFromAttachment] = useState<number | null>(null);
  const [copiedToMediaShareIndex, setCopiedToMediaShareIndex] = useState<number | null>(null);
  const [copiedToMediaShareAttachmentIndex, setCopiedToMediaShareAttachmentIndex] = useState<number | null>(null);

  const sourceCardRef = useRef<HTMLDivElement>(null);
  const mediaPreviewContainerRef = useRef<HTMLDivElement>(null);
  const mediaDisplayRef = useRef<MediaDisplayRef>(null);
  const csamLabel = contentModerationType;
  const currentMboxFetchRef = useRef<MboxFetchHandle | null>(null);
  const activeSourceLoadKeyRef = useRef<string | null>(null);

  const statsContext = useContext(StatsContext);
  const { featureFlags } = useContext(FeaturesContext) || {};
  const { isFullscreen } = useContext(FullScreenContext);
  const { cellId, accessToken } = useCellServiceSession();
  const sawAccessToken = useRef(false);
  if (accessToken) sawAccessToken.current = true;
  const hadAccessToken = sawAccessToken.current;
  const { canSeeDescriptions } = useCsamAccess();
  const jsonMediaDisplayEnabled = useJsonMediaDisplayEnabled();

  const sourceLoadKey = useMemo(
    () => buildSourceLoadKey(selectedSourceIndex, sources[selectedSourceIndex]),
    [
      selectedSourceIndex,
      sources[selectedSourceIndex]?.sourceFilePath,
      sources[selectedSourceIndex]?.analysisFilePath,
      sources[selectedSourceIndex]?.previewFilePath,
      sources[selectedSourceIndex]?.dispatchedFilePath,
      (sources[selectedSourceIndex] as { dispatched_file_path?: string } | undefined)?.dispatched_file_path,
      sources[selectedSourceIndex]?.sourceContext?.dispatchedFilePath,
      sources[selectedSourceIndex]?.sourceContext?.dispatched_file_path,
      getDispatchedFilePath(sources[selectedSourceIndex]),
      sources[selectedSourceIndex]?.sourceContext?.email_index,
      sources[selectedSourceIndex]?.sourceContext?.emailIndex,
      sources[selectedSourceIndex]?.bypassCm,
    ],
  );
  const loadingFileSummary = loadedSourceLoadKey !== sourceLoadKey;

  useEffect(() => {
    setIsCitationExpanded(false);
  }, [sourceLoadKey]);

  const handleCopyAttachmentSourceForTimeline = (attachment: MboxAttachment, attachmentIndex: number) => {
    // convert MboxAttachment to Source
    const source = {
      sourceFilePath: attachment.attachmentUrl,
      sourceContext: {
        attachmentUrl: attachment.attachmentUrl,
      },
    } as Source;
    // Use a negative value to ensure it doesn't match selectedSourceIndex (which is >= 0)
    // This prevents the main source copy button from showing "copied" when only an attachment is copied
    handleCopySourceForTimeline(source, -1 - attachmentIndex);
    setCopiedAttachementIndex(attachmentIndex);
    setTimeout(() => {
      setCopiedAttachementIndex(null);
    }, 1500);
  }

  const handleCreateEventFromAttachment = (source: Source, attachment: MboxAttachment, attachmentIndex: number) => {
    setCreatedEventFromAttachment(attachmentIndex);
    setTimeout(() => {
      setCreatedEventFromAttachment(null);
    }, 2000);
    // convert MboxAttachment to Source
    const sourceFromAttachment = {
      sourceFilePath: attachment.attachmentUrl,
      sourceContext: {
        attachmentUrl: attachment.attachmentUrl,
      },
      content: source.content,
    } as Source;
    handleCreateEventFromSource(sourceFromAttachment, epochTimeSecs);
  }

  const handleCreateEventFromSource = (source: Source, epochTimeSecs: number) => {
    setCreatedEventFromSource(true);
    setTimeout(() => {
      setCreatedEventFromSource(false);
    }, 2000);
    let sourceForTimeline = buildTimelinePrimarySource(source);
    let sourcesList: Source[] = [sourceForTimeline];
    let mediaSources: MediaSource[] = [];
    const seenMediaUrls = new Set<string>();
    const pushUniqueMedia = (media: MediaSource) => {
      const mediaUrl = media.mediaUrl?.trim() || media.mediaSrc?.trim() || '';
      const contentKey = (media.content || '').trim().slice(0, 96);
      const isContentOnlyJson =
        !mediaUrl && media.mediaType === 'json' && !!contentKey;
      if (!mediaUrl && !isContentOnlyJson) return;
      const context = media.mediaContext || {};
      const mediaKey = [
        mediaUrl || `content:${contentKey}`,
        // Distinct content-only citations that share an excerpt prefix.
        !mediaUrl
          ? `src:${(media.sourceUrl || media.sourceFilePath || '').trim()}`
          : '',
        context.pageNumber ?? context.page ?? '',
        context.startTime ?? '',
        context.endTime ?? '',
      ].join('|');
      if (seenMediaUrls.has(mediaKey)) return;
      seenMediaUrls.add(mediaKey);
      mediaSources.push(media);
    };
    const preferredPrimaryDisplay = resolvePreferredMediaDisplayForSource(
      sourceForTimeline,
      sourceForTimeline.fileInfo || fileInfo,
    );
    if (
      preferredPrimaryDisplay.mediaUrl &&
      isUsableMediaType(preferredPrimaryDisplay.mediaType, jsonMediaDisplayEnabled)
    ) {
      const primaryMc = mediaContextForTimelineMediaRow(
        sourceForTimeline,
        preferredPrimaryDisplay.mediaType,
      );
      const canonicalSourceUrl = sourceForTimeline.sourceFilePath || sourceForTimeline.sourceUrl;
      pushUniqueMedia({
        mediaUrl: preferredPrimaryDisplay.mediaUrl,
        mediaType: preferredPrimaryDisplay.mediaType,
        sourceFilePath: sourceForTimeline.sourceFilePath,
        sourceUrl: canonicalSourceUrl,
        originalUrl: canonicalSourceUrl,
        fileInfo: sourceForTimeline.fileInfo || fileInfo || undefined,
        content: sourceForTimeline.content,
        ...(preferredPrimaryDisplay.selectedBy === 'previewFilePath'
          ? { previewFilePath: preferredPrimaryDisplay.mediaUrl }
          : {}),
        ...(primaryMc ? { mediaContext: primaryMc } : {}),
      } as MediaSource);
    } else if (
      isInlineJsonCitationDisplayable(
        sourceForTimeline,
        preferredPrimaryDisplay.mediaType,
        jsonMediaDisplayEnabled,
      )
    ) {
      const primaryMc = mediaContextForTimelineMediaRow(sourceForTimeline, 'json');
      const canonicalSourceUrl = sourceForTimeline.sourceFilePath || sourceForTimeline.sourceUrl;
      pushUniqueMedia({
        mediaType: 'json',
        sourceFilePath: sourceForTimeline.sourceFilePath,
        sourceUrl: canonicalSourceUrl,
        originalUrl: canonicalSourceUrl,
        fileInfo: sourceForTimeline.fileInfo || fileInfo || undefined,
        content: sourceForTimeline.content,
        ...(primaryMc ? { mediaContext: primaryMc } : {}),
      } as MediaSource);
    }
    const isMboxFile =
      sourceForTimeline.sourceFilePath?.endsWith('.mbox') ||
      sourceForTimeline.sourceFilePath?.endsWith('.mbx');
    if (sourceForTimeline.sourceContext?.attachmentUrl || !!attachmentUrl) {
      const attachmentUrlToUse =
        sourceForTimeline.sourceContext?.attachmentUrl || attachmentUrl;
      sourceForTimeline = {
        ...sourceForTimeline,
        sourceContext: {
          ...sourceForTimeline.sourceContext,
          attachmentUrl: attachmentUrlToUse,
        },
      };
      sourcesList = [sourceForTimeline];
      const attachmentMediaType = getMediaType(
        { mediaType: 'unknown', mediaUrl: attachmentUrlToUse } as MediaSource,
        null,
      );
      const attMc = mediaContextForTimelineMediaRow(sourceForTimeline, attachmentMediaType);
      sourcesList.push({
        sourceFilePath: attachmentUrlToUse,
        sourceUrl: attachmentUrlToUse,
      } as Source);
      pushUniqueMedia({
        mediaUrl: attachmentUrlToUse,
        mediaType: attachmentMediaType,
        ...(attMc ? { mediaContext: attMc } : {}),
      } as MediaSource);
    }
    if (isMboxFile && !!attachments && attachments.length > 0) {
      attachments.forEach((attachment) => {
        if (attachment.attachmentUrl && attachment.attachmentUrl !== attachmentUrl) {
          sourcesList.push({
            sourceFilePath: attachment.attachmentUrl,
            sourceUrl: attachment.attachmentUrl,
          } as Source);
          const mediaType = getMediaType(
            { mediaType: 'unknown', mediaUrl: attachment.attachmentUrl } as MediaSource,
            source.fileInfo,
          );
          if (isUsableMediaType(mediaType, jsonMediaDisplayEnabled)) {
            pushUniqueMedia({
              mediaUrl: attachment.attachmentUrl,
              mediaType: mediaType,
            } as MediaSource);
          }
        }
      });
    }
    addToTimeline?.({
      answerText: 'FROM_SOURCE',
      sources: sourcesList,
      media: mediaSources,
      autogen: false,
      epochTimeSecs: epochTimeSecs,
      slideTitle: '',
      slideDescription: sourceForTimeline.content || '',
    } as EventFromChat);
    closeSources();
  }

  useLayoutEffect(() => {
    const loadKey = sourceLoadKey;
    activeSourceLoadKeyRef.current = loadKey;
    setFileInfoLoadError(false);
    setTelemetryReadyKey(null);
    let newFileSummary = null;
    const aSource = sources[selectedSourceIndex];
    const sourceFilePath = aSource?.sourceFilePath;
    const isMboxFile = sourceFilePath?.endsWith('.mbox') || sourceFilePath?.endsWith('.mbx');

    const clearMboxState = () => {
      if (currentMboxFetchRef.current) {
        currentMboxFetchRef.current.abortController.abort();
        currentMboxFetchRef.current = null;
      }
      setAttachmentUrl(null);
      setAttachments(null);
      setOpenAttachmentIndex(null);
      setMboxEmailUrl(null);
    };

    const finishSourceLoad = (
      summary: FileSummary | null,
      sourceHierarchy: HierarchyItem[] | null = null,
      loadRan = true,
    ) => {
      setFileInfo(summary);
      setHierarchy(sourceHierarchy);
      setLoadedSourceLoadKey(loadKey);
      setTelemetryReadyKey(loadRan ? loadKey : null);
    };

    const emailFetch = resolveEmailFetchArgs(aSource);

    // Upstream (evidence locker) already fetched authoritative file-info — do
    // not call again. Chat citations are excluded: any ``fileInfo`` attached
    // upstream can describe the parent container rather than the cited extract,
    // so always (re)load it fresh below via the dispatched-first lookup.
    // Archive-member rows may carry fileInfo whose ``fileUrl`` is still the
    // virtual searchable path — only trust embedded info when it yields a
    // playable storage URL (or the row is not an archive member).
    // ``.eml`` rows still need the tree fetch for attachments, so they continue.
    // ``bypassCm`` must re-fetch: embedded file-info is the CM-safe transcode
    // (previewUrl), not the original asset.
    if (aSource?.fileInfo && !isChatCitationSource(aSource) && !aSource?.bypassCm) {
      const playableEmbedded = resolveFileInfoMediaDisplayUrl(
        aSource.fileInfo,
        (aSource as MediaSource).s3Url || '',
      );
      const isArchiveMember = isArchiveMemberSearchablePath(
        aSource.sourceUrl || aSource.sourceFilePath || '',
      );
      if ((playableEmbedded || !isArchiveMember) && emailFetch?.kind !== 'eml') {
        clearMboxState();
        finishSourceLoad(aSource.fileInfo);
        return;
      }
    }

    const applyCitedAttachment = (
      fetchedAttachments: MboxAttachment[],
      citedAttachmentUrl: string | null,
    ) => {
      setAttachments(fetchedAttachments);
      if (!citedAttachmentUrl) {
        setOpenAttachmentIndex(null);
        return;
      }
      const previewPath = aSource?.previewFilePath?.trim() ?? '';
      const matchedAttachment = fetchedAttachments.find((anAttachment: MboxAttachment) =>
        urlsLikelySameAttachment(anAttachment.attachmentUrl, citedAttachmentUrl),
      );
      const attachmentToOpenIndex = matchedAttachment?.attachmentIndex;
      if (matchedAttachment?.attachmentUrl) {
        setAttachmentUrl(matchedAttachment.attachmentUrl);
      }
      if (
        attachmentToOpenIndex !== undefined &&
        attachmentToOpenIndex !== null &&
        !urlsLikelySameAttachment(citedAttachmentUrl, previewPath)
      ) {
        setOpenAttachmentIndex(attachmentToOpenIndex);
      } else {
        setOpenAttachmentIndex(null);
      }
    };

    if (emailFetch?.kind === 'eml') {
      // .eml extracts (inside zip/mbox or standalone): provenance file-info +
      // tree children — never the junkdrawer ``/mbox`` API.
      if (currentMboxFetchRef.current) {
        currentMboxFetchRef.current.abortController.abort();
      }
      setAttachmentUrl(null);
      setAttachments(null);
      setOpenAttachmentIndex(null);
      setMboxEmailUrl(null);

      const abortController = new AbortController();
      currentMboxFetchRef.current = {
        sourceFilePath: emailFetch.emlUrl,
        emailIndex: null,
        abortController,
      };
      const fetchEml = async () => {
        const payload = await fetchEmailCitation(
          emailFetch,
          { cellId, accessToken },
          abortController.signal,
        );
        if (
          !isActiveMboxFetch(currentMboxFetchRef.current, emailFetch.emlUrl, null) ||
          abortController.signal.aborted
        ) {
          return;
        }
        const emailUrl = payload?.emailUrl?.trim() || '';
        setMboxEmailUrl(emailUrl && !isMboxEmailJsonUrl(emailUrl) ? emailUrl : null);

        const analysisPath = aSource?.analysisFilePath?.trim() || '';
        const analysisIsRealAttachment =
          !!analysisPath &&
          analysisPath !== emailFetch.emlUrl &&
          !urlLooksLikeEml(analysisPath) &&
          !isMboxEmailJsonUrl(analysisPath);
        const citedAttachmentUrl = analysisIsRealAttachment
          ? analysisPath
          : (aSource?.sourceContext?.attachmentUrl as string | undefined) || null;
        if (citedAttachmentUrl) {
          setAttachmentUrl(citedAttachmentUrl);
        }
        if (payload?.attachments && payload.attachments.length > 0) {
          applyCitedAttachment(payload.attachments, citedAttachmentUrl);
        } else {
          setAttachments(null);
          setOpenAttachmentIndex(null);
        }

        if (payload?.fileInfo) {
          finishSourceLoad(payload.fileInfo, payload.hierarchy ?? null);
        } else if (sourceCanDisplayWithoutFileInfo(aSource, jsonMediaDisplayEnabled)) {
          finishSourceLoad(null);
        } else {
          setFileInfoLoadError(true);
          finishSourceLoad(null);
        }
      };
      void fetchEml();
    } else if (isMboxFile) {
      // Cancel any previous mbox fetch to prevent stale data from being set
      if (currentMboxFetchRef.current) {
        currentMboxFetchRef.current.abortController.abort();
      }

      // Clear mbox-specific states immediately when switching between mbox files
      // to prevent displaying stale data from the previously viewed mbox file
      setAttachmentUrl(null);
      setAttachments(null);
      setOpenAttachmentIndex(null);
      setMboxEmailUrl(null);

      const contextEmailIndex = parseSourceContextEmailIndex(aSource?.sourceContext);
      const abortController = new AbortController();
      currentMboxFetchRef.current = {
        sourceFilePath,
        emailIndex: contextEmailIndex,
        abortController,
      };

      const fetchMbox = async () => {
        const fetchSourceFilePath = sourceFilePath;
        const fetchEmailIndex = contextEmailIndex;
        let citedAttachmentUrl = null;
        const analysisFilePath = aSource?.analysisFilePath;
        if (!!analysisFilePath && analysisFilePath !== sourceFilePath) {
          citedAttachmentUrl = analysisFilePath;
          if (isActiveMboxFetch(currentMboxFetchRef.current, fetchSourceFilePath, fetchEmailIndex)) {
            setAttachmentUrl(citedAttachmentUrl);
          }
        } else if (isActiveMboxFetch(currentMboxFetchRef.current, fetchSourceFilePath, fetchEmailIndex)) {
          setAttachmentUrl(null);
        }

        try {
          const preferredEmailUrl = resolvePreferredMediaDisplayForSource(
            aSource as Source,
            newFileSummary,
          ).mediaUrl;
          const mboxData = await fetchMboxData(
            sourceFilePath,
            fetchEmailIndex === null
              ? preferredEmailUrl ||
                aSource?.previewFilePath ||
                (aSource as MediaSource)?.mediaContext?.previewUrl ||
                undefined
              : undefined,
            { cellId, accessToken },
            abortController.signal,
            fetchEmailIndex ?? undefined,
          );

          if (!isActiveMboxFetch(currentMboxFetchRef.current, fetchSourceFilePath, fetchEmailIndex)) {
            return;
          }

          if (mboxData) {
            const emailUrl = mboxData.emailUrl?.trim() || '';
            // Skip junkdrawer ``email.json`` / ``*.json`` body URLs — not displayable email text.
            setMboxEmailUrl(emailUrl && !isMboxEmailJsonUrl(emailUrl) ? emailUrl : null);
          } else {
            setMboxEmailUrl(null);
          }

          if (!!mboxData && !!mboxData.attachments && mboxData.attachments.length > 0) {
            applyCitedAttachment(mboxData.attachments, citedAttachmentUrl);
          } else {
            setAttachments(null);
            setOpenAttachmentIndex(null);
          }
        } finally {
          if (isActiveMboxFetch(currentMboxFetchRef.current, fetchSourceFilePath, fetchEmailIndex)) {
            setLoadedSourceLoadKey(loadKey);
            setTelemetryReadyKey(cellId && accessToken ? loadKey : null);
          }
        }
      };
      fetchMbox();
      setFileInfo(newFileSummary);
      setHierarchy(null);
    } else {
      clearMboxState();

      if (!shouldFetchFileInfoBeforeDisplay(aSource)) {
        // Chat citation already resolves to a supported media type — no file-info.
        finishSourceLoad(null);
      } else {
        // Drop the previous source's provenance hierarchy synchronously so the
        // download dropdown can't sign/open the prior citation's URLs while this
        // source's file-info is still in flight (the header already shows the new
        // source). ``finishSourceLoad`` repopulates it once the fetch resolves.
        setHierarchy(null);
        // Prefer a real storage URL for the lookup. Virtual
        // ``data/bulk/…/archive.zip/inner.png`` paths 404 on ``/files/file-info``.
        const playableEmbedded = aSource?.bypassCm
          ? ''
          : resolveFileInfoMediaDisplayUrl(aSource?.fileInfo, '');
        const candidateLookup =
          (typeof (aSource as MediaSource | undefined)?.s3Url === 'string' &&
          (aSource as MediaSource).s3Url!.trim() &&
          !isArchiveMemberSearchablePath((aSource as MediaSource).s3Url)
            ? (aSource as MediaSource).s3Url!.trim()
            : '') ||
          playableEmbedded ||
          resolveDisplayLookupUrl(
            aSource,
            aSource?.fileInfo,
            resolveFullS3Url(aSource, sourceFilePath ?? ''),
          );
        if (
          !candidateLookup ||
          isArchiveMemberSearchablePath(candidateLookup)
        ) {
          // No resolvable storage URL — do not call file-info with a virtual
          // archive-member path (provenance returns "file not found").
          if (aSource?.fileInfo) {
            finishSourceLoad(aSource.fileInfo);
          } else {
            setFileInfoLoadError(true);
            finishSourceLoad(null);
          }
        } else if (cellId && accessToken) {
          // Fetch the raw envelope so the ``hierarchy`` breadcrumb survives (the
          // ``FileSummary`` projection drops it); ``fileInfo`` still drives media
          // display, ``hierarchy`` powers the download dropdown.
          void getFileInfoBodyFromServer(
            candidateLookup,
            cellId,
            accessToken,
            aSource?.bypassCm,
          ).then((body) => {
            if (activeSourceLoadKeyRef.current !== loadKey) return;
            const fi = fileInfoFromProvenanceBody(body);
            if (!fi && !sourceCanDisplayWithoutFileInfo(aSource, jsonMediaDisplayEnabled)) {
              setFileInfoLoadError(true);
            }
            finishSourceLoad(fi, body?.hierarchy ?? null);
          }).catch(() => {
            if (activeSourceLoadKeyRef.current !== loadKey) return;
            setFileInfoLoadError(true);
            finishSourceLoad(null);
          });
        } else {
          finishSourceLoad(null, null, false);
        }
      }
    }

    // Cleanup function to cancel any pending fetch when component unmounts or effect re-runs
    return () => {
      if (currentMboxFetchRef.current) {
        currentMboxFetchRef.current.abortController.abort();
        currentMboxFetchRef.current = null;
      }
    };
  }, [sourceLoadKey, cellId, hadAccessToken]);

  const selectedSource = sources[selectedSourceIndex];
  // Flagged-image citation chunks are the generated description. Hold them
  // back until file-info resolves, then withhold them entirely when this
  // viewer cannot see CSAM descriptions. A parent-supplied moderation type
  // counts too: chat only passes it for an active finding.
  const citationModeration = fileInfo?.contentModeration?.type
    ? fileInfo.contentModeration
    : contentModerationType
      ? { type: contentModerationType, findingStatus: null }
      : fileInfo?.contentModeration;
  const visibleCitation = loadingFileSummary
    ? null
    : citationForViewer(selectedSource?.content, canSeeDescriptions, citationModeration);
  const fileInfoForViewer = fileSummaryForViewer(fileInfo, canSeeDescriptions);
  const hasCitationSnippet =
    Boolean(visibleCitation?.trim()) &&
    !selectedSource?.location?.includes('/bkb/slow/transcribe/') &&
    !selectedSource?.location?.endsWith('.transcript.txt');
  const hasCitationPage =
    Boolean(selectedSource?.page && !isNaN(parseInt(selectedSource.page)));
  // Cited ``.eml`` extracts use file-info (not the junkdrawer mbox viewer).
  const isMboxSource =
    !isEmlEmailCitation(selectedSource) &&
    (selectedSource?.sourceFilePath?.endsWith('.mbox') ||
      selectedSource?.sourceFilePath?.endsWith('.mbx'));
  const isEmlSource = isEmlEmailCitation(selectedSource);
  const mboxPreviewFilePath = selectedSource?.previewFilePath?.trim() ?? '';
  const citedEmlAttachmentPath = (() => {
    const candidates = [
      attachmentUrl?.trim() ?? '',
      selectedSource?.analysisFilePath?.trim() ?? '',
      typeof selectedSource?.sourceContext?.attachmentUrl === 'string'
        ? selectedSource.sourceContext.attachmentUrl.trim()
        : '',
    ];
    return (
      candidates.find((path) => path && !urlLooksLikeEml(path) && !isMboxEmailJsonUrl(path)) ?? ''
    );
  })();
  const citedEmailPreviewPath = mboxPreviewFilePath || citedEmlAttachmentPath;
  const mboxSourceFilePath = selectedSource?.sourceFilePath?.trim() ?? '';
  const sourceContextEmailIndex = parseSourceContextEmailIndex(selectedSource?.sourceContext);
  const resolvedMboxEmailUrl = mboxEmailUrl?.trim() ?? '';
  const emlHasDistinctCitedPreview =
    isEmlSource &&
    citedEmailPreviewPath !== '' &&
    citedEmailPreviewPath !== resolvedMboxEmailUrl &&
    !urlLooksLikeEml(citedEmailPreviewPath) &&
    !isMboxEmailJsonUrl(citedEmailPreviewPath);
  /** Cited preview/attachment file from previewFilePath. */
  const showMboxPreviewMediaDisplay =
    (isMboxSource &&
      mboxPreviewFilePath !== '' &&
      mboxPreviewFilePath !== mboxSourceFilePath) ||
    emlHasDistinctCitedPreview;
  /** Email body below preview when fetched email differs from cited preview attachment. */
  const showMboxEmailMediaDisplay =
    (isMboxSource || isEmlSource) &&
    resolvedMboxEmailUrl !== '' &&
    !isMboxEmailJsonUrl(resolvedMboxEmailUrl) &&
    resolvedMboxEmailUrl !== citedEmailPreviewPath;
  const showMboxStyleMediaPanes =
    isMboxSource || showMboxPreviewMediaDisplay || showMboxEmailMediaDisplay;
  // Capture memory is session-global, while this citation belongs to one
  // answer row and source slot. Keep that scope on the MediaSource so the
  // app-level fullscreen copy retains it across the owner/viewer handoff.
  const captureCitationScope = getAnswerSourceCaptureScope({
    caseId,
    epochTimeSecs,
    answerIndex,
    sourceIndex: selectedSourceIndex,
  });

  const mboxPreviewMediaItem = useMemo((): MediaSource | null => {
    if (!showMboxPreviewMediaDisplay || !citedEmailPreviewPath) return null;
    return {
      sourceFilePath: citedEmailPreviewPath,
      sourceUrl: citedEmailPreviewPath,
      dispatchedFilePath: citedEmailPreviewPath,
      mediaUrl: citedEmailPreviewPath,
      mediaType: getMediaType(
        { mediaType: 'unknown', mediaUrl: citedEmailPreviewPath } as MediaSource,
        fileInfo,
      ),
      fileInfo: fileInfoForViewer ?? undefined,
      captureCitationScope,
      mediaContext: {
        ...(selectedSource?.sourceContext ?? {}),
        previewUrl: citedEmailPreviewPath,
      },
    } as MediaSource;
  }, [
    showMboxPreviewMediaDisplay,
    citedEmailPreviewPath,
    selectedSource?.sourceContext,
    fileInfoForViewer,
    captureCitationScope,
  ]);

  const mboxEmailMediaItem = useMemo((): MediaSource | null => {
    if (!showMboxEmailMediaDisplay) return null;
    return {
      mediaUrl: resolvedMboxEmailUrl,
      mediaType: 'text',
      srcToUse: resolvedMboxEmailUrl,
      originalUrl: resolvedMboxEmailUrl,
      fileInfo: fileInfoForViewer ?? undefined,
      captureCitationScope,
    } as MediaSource;
  }, [showMboxEmailMediaDisplay, resolvedMboxEmailUrl, fileInfoForViewer, captureCitationScope]);

  const visibleAttachments = useMemo(() => {
    if (!attachments?.length) return attachments;
    return attachments.filter((attachment) => {
      const attachmentUrl = attachment.attachmentUrl?.trim() ?? '';
      if (!attachmentUrl) return false;
      if (isMboxEmailJsonUrl(attachmentUrl)) return false;
      return (
        !urlsLikelySameAttachment(attachmentUrl, citedEmailPreviewPath) &&
        !urlsLikelySameAttachment(attachmentUrl, resolvedMboxEmailUrl)
      );
    });
  }, [attachments, citedEmailPreviewPath, resolvedMboxEmailUrl]);

  const sourceCardMediaItem = useMemo(
    () => {
      const source = fullScreenSource ?? sources[selectedSourceIndex];
      const sourceIsMbox =
        !isEmlEmailCitation(source) &&
        (source.sourceFilePath?.endsWith('.mbox') || source.sourceFilePath?.endsWith('.mbx'));
      const preferredDisplay = resolvePreferredMediaDisplayForSource(
        source,
        fileInfo,
      );
      const playableFromFileInfo = resolveFileInfoMediaDisplayUrl(fileInfo, '');
      const citationAttachedUrl = (
        (source as MediaSource).mediaUrl ||
        (source as MediaSource).mediaSrc ||
        ''
      ).trim();
      const citationAttachedType = citationAttachedUrl
        ? getMediaType(
            {
              mediaType: (source as MediaSource).mediaType || 'unknown',
              mediaUrl: citationAttachedUrl,
            } as MediaSource,
            fileInfo,
          )
        : ('unknown' as MediaType);
      const citationUrlDisplayable =
        !!citationAttachedUrl &&
        !isArchiveMemberSearchablePath(citationAttachedUrl) &&
        isUsableMediaType(
          citationAttachedType !== 'unknown'
            ? citationAttachedType
            : (source as MediaSource).mediaType,
          jsonMediaDisplayEnabled,
        );
      const preferredUrlDisplayable =
        !!preferredDisplay.mediaUrl &&
        !isArchiveMemberSearchablePath(preferredDisplay.mediaUrl) &&
        isUsableMediaType(preferredDisplay.mediaType, jsonMediaDisplayEnabled);
      const rawDisplayUrl = sourceIsMbox
        ? source.sourceFilePath
        : preferredUrlDisplayable
          ? preferredDisplay.mediaUrl
          : citationUrlDisplayable
            ? citationAttachedUrl
            : playableFromFileInfo || undefined;
      // Never feed MediaDisplay a virtual ``…/archive.zip/inner`` path — proxy 404s.
      const displayUrl =
        rawDisplayUrl && !isArchiveMemberSearchablePath(rawDisplayUrl)
          ? rawDisplayUrl
          : playableFromFileInfo && !isArchiveMemberSearchablePath(playableFromFileInfo)
            ? playableFromFileInfo
            : undefined;
      let mediaType = sourceIsMbox
        ? getMediaType(source, fileInfo)
        : preferredUrlDisplayable
          ? preferredDisplay.mediaType
          : citationUrlDisplayable
            ? ((citationAttachedType !== 'unknown'
                ? citationAttachedType
                : (source as MediaSource).mediaType) || preferredDisplay.mediaType)
            : (preferredDisplay.mediaType || getMediaType(source, fileInfo));
      // Content-only JSON (flag on): keep mediaType json even when preferred has no URL.
      if (
        !sourceIsMbox &&
        !displayUrl &&
        isInlineJsonCitationDisplayable(source, mediaType, jsonMediaDisplayEnabled)
      ) {
        mediaType = 'json';
      }
      const mediaPathForDisplay = displayUrl || undefined;
      const canonicalS3LookupUrl = resolveDisplayLookupUrl(source, fileInfo);
      const canonicalSourcePath =
        sources[selectedSourceIndex].sourceFilePath?.trim() ||
        sources[selectedSourceIndex].sourceUrl?.trim() ||
        '';
      const isCanonicalMetaBundle = isMetaBundleFilename(canonicalSourcePath);
      const previewPathForMeta =
        sources[selectedSourceIndex].previewFilePath?.trim() ||
        (preferredDisplay.selectedBy === 'previewFilePath'
          ? preferredDisplay.mediaUrl?.trim()
          : undefined) ||
        mediaPathForDisplay;

      return {
        ...sources[selectedSourceIndex],
        captureCitationScope,
        content: visibleCitation ?? undefined,
        fileInfo: fileInfoForViewer ?? undefined,
        ...(canonicalS3LookupUrl && !isArchiveMemberSearchablePath(canonicalS3LookupUrl)
          ? { s3Url: canonicalS3LookupUrl }
          : playableFromFileInfo
            ? { s3Url: playableFromFileInfo }
            : {}),
        originalUrl: canonicalSourcePath || sources[selectedSourceIndex].sourceFilePath,
        sourceFilePath: sourceIsMbox
          ? sources[selectedSourceIndex].sourceFilePath
          : (mediaPathForDisplay || sources[selectedSourceIndex].sourceFilePath),
        sourceUrl: sourceIsMbox
          ? sources[selectedSourceIndex].sourceUrl
          : isCanonicalMetaBundle
            ? canonicalSourcePath
            : (mediaPathForDisplay || sources[selectedSourceIndex].sourceUrl),
        previewFilePath: sourceIsMbox
          ? sources[selectedSourceIndex].previewFilePath
          : previewPathForMeta,
        transcriptUrl:
          resolveTranscriptUrlFromSourceContext(
            sources[selectedSourceIndex].sourceContext as Record<string, unknown> | undefined,
          ) ??
          resolveTranscriptUrlFromFileInfo(fileInfo) ??
          resolveTranscriptUrlFromFileInfo(sources[selectedSourceIndex].fileInfo) ??
          (isTranscriptJsonLocation(sources[selectedSourceIndex].location)
            ? sources[selectedSourceIndex].location
            : undefined),
        mediaUrl: displayUrl,
        mediaSrc: mediaPathForDisplay,
        srcToUse: mediaPathForDisplay,
        mediaType,
        mediaContext: {
          ...(() => {
            const base =
              fullScreenSource?.sourceContext ?? sources[selectedSourceIndex].sourceContext ?? {};
            const { pageNumber: _stalePageFromContext, ...rest } = base as Record<string, unknown>;
            return rest;
          })(),
          transcriptPosition: 'below',
          showTranscriptMarkers: true,
          showTranscript: true,
          htmlChunk: visibleCitation
            ? visibleCitation.match(/page \d+/i)?.[0] || ''
            : '',
          pageNumber: parseCitationPageFromSourceLike(
            fullScreenSource ? fullScreenSource : sources[selectedSourceIndex],
          ),
          ...((() => {
            const upstreamPageCount =
              fullScreenSource?.pageCount ??
              sources[selectedSourceIndex].pageCount ??
              fileInfo?.pageCount;
            return typeof upstreamPageCount === 'number' && upstreamPageCount > 0
              ? { pageCount: upstreamPageCount }
              : {};
          })()),
          startTime: fullScreenSource
            ? fullScreenSource.sourceContext?.startTime || 0
            : sources[selectedSourceIndex].sourceContext?.startTime || 0,
          previewUrl: sourceIsMbox
            ? source.previewFilePath || source.derived_object_url
            : fullScreenSource
              ? resolvePreferredMediaDisplayForSource(fullScreenSource, fileInfo).mediaUrl
              : resolvePreferredMediaDisplayForSource(
                  sources[selectedSourceIndex],
                  fileInfo,
                ).mediaUrl,
        },
      } as MediaSource;
    },
    [
      sources,
      selectedSourceIndex,
      fileInfo,
      fileInfoForViewer,
      visibleCitation,
      fullScreenSource,
      jsonMediaDisplayEnabled,
      captureCitationScope,
    ],
  );

  /** Meta-file source (Social Media Viewer button shown): expand View Details and render no MediaDisplay (there is no media to preview). */
  const sourceCardIsMetaFile = !loadingFileSummary && isMetaFile(sourceCardMediaItem);

  const mboxPreviewMediaType = mboxPreviewMediaItem?.mediaType;
  const mboxPreviewDisplaySupported =
    showMboxPreviewMediaDisplay && isUsableMediaType(mboxPreviewMediaType, jsonMediaDisplayEnabled);
  /** Default mbox viewer (first email via MediaDisplay) when no separate preview/email panes apply. */
  const showMboxDefaultMediaDisplay =
    isMboxSource &&
    !loadingFileSummary &&
    !mboxPreviewDisplaySupported &&
    !showMboxEmailMediaDisplay;
  const nonMboxMediaDisplaySupported =
    !showMboxStyleMediaPanes && isUsableMediaType(sourceCardMediaItem.mediaType, jsonMediaDisplayEnabled);
  const showAnyMediaDisplay =
    mboxPreviewDisplaySupported ||
    showMboxEmailMediaDisplay ||
    showMboxDefaultMediaDisplay ||
    nonMboxMediaDisplaySupported;
  const showUnsupportedMediaMessage =
    !loadingFileSummary && !showAnyMediaDisplay && !sourceCardIsMetaFile;
  // A failed file-info lookup keeps its own error. This is the case that used
  // to show the container / mbox / "no displayable media" copy.
  const citationHasNoViewableMedia = showUnsupportedMediaMessage && !fileInfoLoadError;
  const previewTelemetryEligible = isPreviewTelemetryEligible({
    loadingFileSummary,
    telemetryReadyKey,
    sourceLoadKey,
  });
  // Expanding the citation and logging it wait for a lookup that really ran. A card that mounts
  // with a cell but no token settles to "no media" first, and the reload on the token fixes it.
  const citationNoViewableMediaConfirmed = citationHasNoViewableMedia && previewTelemetryEligible;

  useEffect(() => {
    if (sourceCardIsMetaFile || citationNoViewableMediaConfirmed) {
      setIsCitationExpanded(true);
    }
  }, [sourceCardIsMetaFile, citationNoViewableMediaConfirmed]);

  const loggedUndisplayableKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!citationNoViewableMediaConfirmed) return;
    if (loggedUndisplayableKeyRef.current === sourceLoadKey) return;
    loggedUndisplayableKeyRef.current = sourceLoadKey;
    const source = sources[selectedSourceIndex];
    if (!source) return;
    void trackEvent('undisplayableCitation', {
      user: statsContext?.userInfo?.userId || 'unknown',
      case_id: caseId || statsContext?.userInfo?.caseId || 'unknown',
      session_id: statsContext?.userInfo?.sessionId || 'unknown',
      chat_key: chatKey,
      epoch_time_secs: epochTimeSecs,
      citation: citationForTelemetry(source),
    });
  }, [
    citationNoViewableMediaConfirmed,
    sourceLoadKey,
    sources,
    selectedSourceIndex,
    statsContext,
    caseId,
    chatKey,
    epochTimeSecs,
  ]);

  const [previewContainerSize, setPreviewContainerSize] = useState({ width: 0, height: 0 });
  /**
   * Pin media-column width for AR height so content/scrollbar feedback cannot
   * oscillate. Re-pin when the *outer* scrollport/card width changes (column
   * resizer, window resize) — not on every self-ResizeObserver tick.
   */
  const pinnedMediaPreviewWidthRef = useRef<number | null>(null);
  const pinnedMediaPreviewHeightRef = useRef<number | null>(null);
  const lastOuterLayoutWidthRef = useRef<number | null>(null);

  // AR sizing uses a pinned width — active whenever we are not filling a parent flex slot.
  const pinMediaPreviewWidth = !fillPreviewContainer;

  useLayoutEffect(() => {
    const el = mediaPreviewContainerRef.current;
    if (!el) return;

    // New source / citation chrome / show-media: allow one fresh measure, then pin.
    pinnedMediaPreviewWidthRef.current = null;
    pinnedMediaPreviewHeightRef.current = null;
    lastOuterLayoutWidthRef.current = null;

    const outerLayoutEl = (): HTMLElement =>
      (el.closest('[data-source-card-scrollport]') as HTMLElement | null)
      ?? sourceCardRef.current
      ?? (el.parentElement as HTMLElement | null)
      ?? el;

    const commitSize = (width: number, height = 0) => {
      if (width <= 0) return;
      setPreviewContainerSize((prev) =>
        prev.width === width && prev.height === height ? prev : { width, height },
      );
    };

    // Fill mode measures own content height; otherwise fit to the scrollport.
    // Return null when unavailable — never pin 0, or remasurement stalls until
    // window resize (scrollportMediaHeight stays unset → no containerHeight).
    const measureHeight = (): number | null => {
      if (fillPreviewContainer) {
        const h = Math.floor(el.getBoundingClientRect().height);
        return h > 0 ? h : null;
      }
      return availableScrollportHeight(el);
    };

    const pinPositiveHeight = (height: number | null) => {
      if (height != null && height > 0) {
        pinnedMediaPreviewHeightRef.current = height;
      }
    };

    const syncSize = () => {
      const rect = el.getBoundingClientRect();
      const measured = Math.floor(rect.width);
      if (measured <= 0) return;

      if (!pinMediaPreviewWidth) {
        commitSize(measured, measureHeight() ?? 0);
        return;
      }

      const outer = outerLayoutEl();
      const outerWidth = Math.floor(outer.getBoundingClientRect().width);
      const outerChanged =
        lastOuterLayoutWidthRef.current == null
        || outerWidth !== lastOuterLayoutWidthRef.current;

      if (outerChanged) {
        lastOuterLayoutWidthRef.current = outerWidth;
        pinnedMediaPreviewWidthRef.current = measured;
        // Clear stale pin so a failed measure does not lock height at 0.
        pinnedMediaPreviewHeightRef.current = null;
        pinPositiveHeight(measureHeight());
        commitSize(measured, pinnedMediaPreviewHeightRef.current ?? 0);
        return;
      }

      if (pinnedMediaPreviewWidthRef.current == null) {
        pinnedMediaPreviewWidthRef.current = measured;
      }
      if (pinnedMediaPreviewHeightRef.current == null) {
        pinPositiveHeight(measureHeight());
      }
      commitSize(
        pinnedMediaPreviewWidthRef.current,
        pinnedMediaPreviewHeightRef.current ?? 0,
      );
    };

    const onWindowResize = () => {
      if (pinMediaPreviewWidth) {
        pinnedMediaPreviewWidthRef.current = null;
        pinnedMediaPreviewHeightRef.current = null;
        lastOuterLayoutWidthRef.current = null;
      }
      syncSize();
    };

    syncSize();

    let observer: ResizeObserver | null = null;
    if (pinMediaPreviewWidth) {
      // Watch the scrollport/card (column drag) and ``el`` (first flex settle).
      observer = new ResizeObserver(syncSize);
      observer.observe(outerLayoutEl());
      observer.observe(el);
    } else {
      observer = new ResizeObserver(syncSize);
      observer.observe(el);
    }
    window.addEventListener('resize', onWindowResize);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', onWindowResize);
    };
  }, [
    loadingFileSummary,
    showAnyMediaDisplay,
    isCitationExpanded,
    hasCitationSnippet,
    pinMediaPreviewWidth,
    fillPreviewContainer,
    sourceLoadKey,
  ]);

  // Full column width; height from media aspect ratio (Letter for docs, else 1:1).
  const mediaContainerWidth = previewContainerSize.width > 0
    ? previewContainerSize.width
    : undefined;

  const fillPreviewContainerHeight =
    fillPreviewContainer && previewContainerSize.height > 0
      ? previewContainerSize.height
      : undefined;

  // Non-fill: prefer scrollport-fit height; AR fallback when measure is not ready
  // yet (or no scrollport ancestor), so MediaDisplay always gets a containerHeight.
  const scrollportMediaHeight =
    !fillPreviewContainer && previewContainerSize.height > 0
      ? previewContainerSize.height
      : undefined;

  const mboxPreviewHeight =
    !fillPreviewContainer
    && mediaContainerWidth
    && mboxPreviewDisplaySupported
    && !isContinuousMediaType(mboxPreviewMediaType)
      ? (scrollportMediaHeight
        ?? fitHeightForMediaWidth(mediaContainerWidth, mboxPreviewMediaType))
      : undefined;
  const mboxEmailHeight =
    !fillPreviewContainer && mediaContainerWidth && showMboxEmailMediaDisplay
      ? (scrollportMediaHeight ?? fitHeightForMediaWidth(mediaContainerWidth, 'text'))
      : undefined;
  const mboxDefaultHeight =
    !fillPreviewContainer
    && mediaContainerWidth
    && showMboxDefaultMediaDisplay
    && !isContinuousMediaType(sourceCardMediaItem.mediaType)
      ? (scrollportMediaHeight
        ?? fitHeightForMediaWidth(mediaContainerWidth, sourceCardMediaItem.mediaType))
      : undefined;
  // Docs/images: scrollport-fit height (AR fallback). Video/audio: width only so
  // transcript can extend the card.
  const nonMboxHeight =
    !fillPreviewContainer
    && mediaContainerWidth
    && nonMboxMediaDisplaySupported
    && !isContinuousMediaType(sourceCardMediaItem.mediaType)
      ? (scrollportMediaHeight
        ?? fitHeightForMediaWidth(mediaContainerWidth, sourceCardMediaItem.mediaType))
      : undefined;

  const mediaContainerHeight =
    fillPreviewContainerHeight
    ?? mboxPreviewHeight
    ?? mboxEmailHeight
    ?? mboxDefaultHeight
    ?? nonMboxHeight;

  const wrapPreviewMedia = (node: React.ReactNode) => (
    <MediaPreviewSlot
      ref={mediaPreviewContainerRef}
      fillContainer={fillPreviewContainer}
      mediaItem={sourceCardMediaItem}
      minHeight={mediaContainerHeight}
      isContinuousMedia={isContinuousMediaType(sourceCardMediaItem.mediaType)}
    >
      {node}
    </MediaPreviewSlot>
  );

  const isCreateEventFromContainerSource = isContainerSelfSource(selectedSource, fileInfo);
  const isContainerNonMbox =
    !!selectedSource && isContainerLikeSource(selectedSource, fileInfo, { includeMbox: false });
  const previewFallback = resolveSourceCardFallback({
    fileInfoLoadError,
    showUnsupportedMediaMessage,
    isMboxType: isMboxTypeSource(selectedSource, fileInfo),
    isContainerNonMbox,
  });
  const reportedOpeningRef = useRef<string | null>(null);
  const citedPreviewRef = getDispatchedFilePath(selectedSource) || selectedSource?.sourceFilePath || null;
  const previewOpening = openingIdentity(citedPreviewRef, selectedSource?.bypassCm ? 'original' : 'default');
  useEffect(() => {
    if (reportedOpeningRef.current !== previewOpening) reportedOpeningRef.current = null;
    if (!previewTelemetryEligible || previewFallback === null) return;
    if (reportedOpeningRef.current === previewOpening) return;
    reportedOpeningRef.current = previewOpening;
    const identity = resolvePreviewIdentity({
      citedRef: citedPreviewRef,
      fileUuid: fileInfo?.fileUuid,
      fileUrl: fileInfo?.fileUrl,
    });
    const describesCitedFile = identity.identityKind === 'file_uuid';
    reportUnsupportedPreview({
      state: previewFallback,
      identity,
      caseId,
      mime: describesCitedFile ? fileInfo?.mimeType : null,
      extension: describesCitedFile && fileInfo?.extension ? fileInfo.extension : extensionFromRef(citedPreviewRef),
    });
  }, [previewTelemetryEligible, previewFallback, previewOpening]);

  // The "Item:" label names the responsive file cited inside a container.
  // It comes from the dispatched object's real basename — the extracted object
  // the citation resolves to. The previewFilePath is only a renderable proxy
  // (e.g. a .webp generated for a .heic) so it must not name the Item.
  const itemDispatchedFilePath = getDispatchedFilePath(selectedSource);
  const itemFileName = itemDispatchedFilePath
    ? itemDispatchedFilePath.split('?')[0].split('/').pop() || ''
    : '';

  const resolveSourceForActions = (source: Source): Source =>
    resolveSourceForActionsFromCitation(source, hierarchy);

  /** Toolbar identity for scope: keep fetched file-info so searchablePath survives action resolve. */
  const sourceForScopeActions = (source: Source): Source => ({
    ...resolveSourceForActions(source),
    ...(fileInfo ? { fileInfo } : {}),
  });

  const contentModerationPreviewProps = {
    ...(onToggleObfuscatedPreview != null
      ? {
          showObfuscatedPreview: showObfuscatedPreview ?? true,
          onToggleObfuscatedPreview,
        }
      : {}),
    ...(csamLabel ? { contentModerationType: csamLabel } : {}),
  };
  const sourceCardMediaLayoutProps = fillPreviewContainer
    ? { fillContainer: true as const }
    : {};

  // LLM description is limited-user and above. The CSAM tag itself is
  // gated at call sites via ``canSeeCsamTags`` (tenant-default and above).
  const showImageDescription =
    canSeeDescriptions && !!contentModerationType && !loadingFileSummary;
  const imageDescription = showImageDescription
    ? (contentModerationImageDescription ??
      fileInfo?.contentModeration?.imageDescription ??
      null)
    : null;

  const renderSourceCardMedia = () => {
    if (showMboxStyleMediaPanes) {
      return (
        <>
          {mboxPreviewDisplaySupported && mboxPreviewMediaItem && (
            <MediaDisplay
              ref={mediaDisplayRef}
              key={`mbox-preview-${citedEmailPreviewPath}`}
              isPresentation={false}
              isSourceCard={true}
              mediaItem={mboxPreviewMediaItem}
              containerHeight={mboxPreviewHeight}
              containerWidth={mediaContainerWidth}
              caseId={caseId}
              hideMetaSocialViewerButton={true}
              {...contentModerationPreviewProps}
              {...sourceCardMediaLayoutProps}
            />
          )}
          {showMboxEmailMediaDisplay && mboxEmailMediaItem && (
            <MediaDisplay
              ref={mboxPreviewDisplaySupported ? undefined : mediaDisplayRef}
              key={`mbox-email-${selectedSource.sourceFilePath}-${sourceContextEmailIndex ?? 'default'}`}
              isPresentation={false}
              isSourceCard={true}
              mediaItem={mboxEmailMediaItem}
              containerHeight={mboxEmailHeight}
              containerWidth={mediaContainerWidth}
              caseId={caseId}
              hideMetaSocialViewerButton={true}
              {...contentModerationPreviewProps}
              {...sourceCardMediaLayoutProps}
            />
          )}
          {showMboxDefaultMediaDisplay && (
            <MediaDisplay
              ref={mediaDisplayRef}
              key={`mbox-default-${selectedSource.sourceFilePath}`}
              isPresentation={false}
              isSourceCard={true}
              mediaItem={sourceCardMediaItem}
              containerHeight={mboxDefaultHeight}
              containerWidth={mediaContainerWidth}
              caseId={caseId}
              hideMetaSocialViewerButton={true}
              {...contentModerationPreviewProps}
              {...sourceCardMediaLayoutProps}
            />
          )}
        </>
      );
    }
    if (sourceCardIsMetaFile) {
      return null;
    }
    return (
      <MediaDisplay
        ref={mediaDisplayRef}
        key={`${selectedSource.sourceFilePath}-${selectedSource.sourceContext?.startTime || 0}`}
        isPresentation={false}
        isSourceCard={true}
        mediaItem={sourceCardMediaItem}
        containerHeight={mediaContainerHeight}
        containerWidth={mediaContainerWidth}
        caseId={caseId}
        hideMetaSocialViewerButton={true}
        citationScrollKey={sourceLoadKey}
        {...contentModerationPreviewProps}
        {...sourceCardMediaLayoutProps}
      />
    );
  };

  const pdfPreviewDownload = hasHierarchyMenu(hierarchy, loadingFileSummary)
    ? markdownPdfPreviewItem(fileInfo, hierarchy)
    : null;

  return (
    <Card
      data-testid="SourceCard"
      ref={sourceCardRef}
      classNames={{
        base: fillPreviewContainer
          ? 'relative flex min-h-full w-full flex-col bg-[var(--color-ink-090)] p-2 sm:px-4 sm:py-2 rounded-sm sm:rounded-2xl shadow-xl'
          : // overflow-x only — y must stay visible so tall video+transcript grows the
            // card and the answer-item scrollport can reach the transcript bottom.
            'flex-grow relative bg-[var(--color-ink-090)] p-2 sm:px-4 sm:py-2 rounded-sm sm:rounded-2xl shadow-xl min-h-[200px] w-full overflow-x-hidden overflow-y-visible',
        header: 'flex flex-col gap-1 items-stretch m-0 p-0 border-b-[1px] border-white/70 border-solid pb-2 mb-2 shrink-0',
        body: fillPreviewContainer
          ? 'relative flex min-h-0 flex-1 flex-col p-0 m-0'
          : 'relative flex h-auto flex-col p-0 m-0 min-h-[200px] overflow-x-hidden overflow-y-visible',
      }}
      style={
        fillPreviewContainer
          ? { minHeight: '100%', alignSelf: 'stretch' }
          : {
              height: 'auto',
              alignSelf: 'flex-start',
              // Beat HeroUI Card default overflow:hidden so transcript drag grows layout.
              overflow: 'visible',
            }
      }
    >
      <CardHeader>
        <div className='flex flex-row w-full items-center gap-2'>
          <div className='flex-1 min-w-0 flex flex-col gap-0.5'>
            <div
              data-testid="SourceLabel"
              className="flex min-w-0 flex-row flex-wrap items-center gap-1.5 text-white/90 select-text cursor-default"
            >
              <span className={`min-w-0 break-all font-semibold text-[16px] leading-tight ${PATH_DISPLAY_WHITESPACE_CLASSNAME}`}>
                {sources[selectedSourceIndex].label
                  || extractPathAfterBulk(
                    sources[selectedSourceIndex].sourceFilePath
                      || sources[selectedSourceIndex].sourceUrl
                      || sources[selectedSourceIndex].truncatedFilePath
                      || '',
                    true,
                  ).truncatedFilePath
                  || sources[selectedSourceIndex].truncatedFilePath}
              </span>
              {headerPrefix ?? (csamLabel ? <CsamDetectionPill type={csamLabel} /> : null)}
            </div>
            {!!itemFileName && (
              <div className="flex flex-row gap-1 text-white/90 select-text cursor-default">
                <span className='font-semibold leading-tight text-[16px] text-white/70'>{t('sources.item')} </span>
                <span className={`font-semibold leading-tight text-[16px] break-all ${PATH_DISPLAY_WHITESPACE_CLASSNAME}`}>
                  {itemFileName}
                </span>
              </div>
            )}
          </div>
          <div className='flex-shrink-0 flex flex-row items-center'>
            <Button
              iconOnly
              size="sm"
              style="text"
              color="light"
              background="inverse"
              aria-label={t('sources.closeCard')}
              data-testid="close-source-button"
              onClick={closeSources}
              onTouchEnd={(e) => {
                e.preventDefault();
                closeSources();
              }}
            >
              <XIcon />
            </Button>
          </div>
        </div>
        <div className='box-border flex flex-row gap-2 items-center text-white/70 rounded-sm pt-1 border-t-[1px] border-white/70 border-solid'>
          {hasHierarchyMenu(hierarchy, loadingFileSummary) ? (
            // Citations resolved to an extract inside a container expose the
            // full provenance hierarchy: index 0 is the root source file, the
            // remaining entries are the enclosing subdocs / extracted target.
            // Offer each as a separate download. Gated on ``!loadingFileSummary``
            // so a switch to a new source can't leave the prior citation's
            // entries openable while its file-info is still loading.
            <HierarchyActionMenu
              hierarchy={hierarchy}
              trailingItems={
                pdfPreviewDownload
                  ? [{ item: pdfPreviewDownload, description: t('sources.pdfPreview') }]
                  : undefined
              }
              title={t('sources.downloadFile')}
              ariaLabel={t('sources.downloadHierarchy')}
              onSelect={({ item }) => {
                handleDownload({ sourceFilePath: item.s3Url, sourceUrl: item.s3Url } as Source);
                recordStat('DownloadSourceButtonClicked', undefined, statsContext);
              }}
            >
              <Button
                iconOnly
                size="sm"
                style="text"
                color="light"
                background="inverse"
                aria-label={t('sources.downloadFiles')}
              >
                <DownloadSimpleIcon data-testid={`download-source-trigger-icon-${answerIndex}-${selectedSourceIndex}`} />
              </Button>
            </HierarchyActionMenu>
          ) : (
            <TpTooltip
              content={t('sources.download')}
              placement="bottom"
              delayDuration={0}
              sideOffset={2}
            >
              <Button
                iconOnly
                size="sm"
                style="text"
                color="light"
                background="inverse"
                onClick={(event) => {
                  const resolvedSource = resolveSourceForActions(sources[selectedSourceIndex]);
                  handleDownload(resolvedSource);
                  recordStat(
                    'DownloadSourceButtonClicked',
                    event,
                    statsContext                );
                }}
                onTouchEnd={() => {
                  const resolvedSource = resolveSourceForActions(sources[selectedSourceIndex]);
                  handleDownload(resolvedSource);
                }}
                disabled={isRestrictedFileType(resolveSourceForActions(sources[selectedSourceIndex]))}
                aria-label={t('sources.download')}
              >
                <DownloadSimpleIcon data-testid={`download-source-trigger-icon-${answerIndex}-${selectedSourceIndex}`} />
              </Button>
            </TpTooltip>
          )}
          <TpTooltip
            content={t('sources.addToScope')}
            placement="bottom"
            delayDuration={0}
            sideOffset={2}
          >
            <div className='relative h-[28px] w-[28px]'>
              <Button
                iconOnly
                size="sm"
                style={addedSource === selectedSourceIndex ? "solid" : "text"}
                color={addedSource === selectedSourceIndex ? "accent" : "light"}
                background="inverse"
                aria-label={t('sources.addToScope')}
                data-testid="add-source-to-scope-button"
                onClick={async (event) => {
                  setIsExpanded?.(true);
                  await new Promise(resolve => setTimeout(resolve, 750));
                  handleAddSourceToScope(
                    sourceForScopeActions(sources[selectedSourceIndex]),
                    selectedSourceIndex,
                  );
                  recordStat(
                    'AddSourceToScopeButtonClicked',
                    event,
                    statsContext                  );
                }}
                onTouchEnd={async () => {
                  setIsExpanded?.(true);
                  await new Promise(resolve => setTimeout(resolve, 750));
                  handleAddSourceToScope(
                    sourceForScopeActions(sources[selectedSourceIndex]),
                    selectedSourceIndex,
                  );
                }}
              >
                {addedSource === selectedSourceIndex ?
                  (<CheckIcon />) :
                  (<FunnelIcon data-testid={`add-source-to-scope-trigger-icon-${answerIndex}-${selectedSourceIndex}`} />)
                }
              </Button>
              {addedSource === selectedSourceIndex ? null : (<PlusIcon size={10} className='absolute bottom-0 right-0 fill-white pointer-events-none' />)}
            </div>
          </TpTooltip>
          <TpTooltip
            content={t('sources.copyPath')}
            placement="bottom"
            delayDuration={0}
            sideOffset={2}
          >
            <Button
              iconOnly
              size="sm"
              style={copiedSource === selectedSourceIndex ? "solid" : "text"}
              color={copiedSource === selectedSourceIndex ? "accent" : "light"}
              background="inverse"
              aria-label={t('sources.copyPath')}
              data-testid="copy-source-path-button"
              onClick={(event) => {
                const resolvedSource = resolveSourceForActions(sources[selectedSourceIndex]);
                handleCopySourceForTimeline(resolvedSource, selectedSourceIndex);
                recordStat(
                  'CopySourcePathButtonClicked',
                  event,
                  statsContext                );
              }}
              onTouchEnd={() => {
                const resolvedSource = resolveSourceForActions(sources[selectedSourceIndex]);
                handleCopySourceForTimeline(resolvedSource, selectedSourceIndex);
              }}
            >
              {copiedSource === selectedSourceIndex ?
                (<CheckIcon />) :
                (<FileCloudIcon data-testid={`copy-source-trigger-icon-${answerIndex}-${selectedSourceIndex}`} />)
              }
            </Button>
          </TpTooltip>
          {handleCopyToMediaShare &&
            sourceCanAddToFavorites(
              resolveSourceForActions(sources[selectedSourceIndex]),
              jsonMediaDisplayEnabled,
            ) && (
            <TpTooltip
              content={copiedToMediaShareIndex === selectedSourceIndex ? t('sources.added') : t('sources.addToFavorites')}
              placement="bottom"
              delayDuration={0}
              sideOffset={2}
            >
              <Button
                iconOnly
                size="sm"
                style={copiedToMediaShareIndex === selectedSourceIndex ? "solid" : "text"}
                color={copiedToMediaShareIndex === selectedSourceIndex ? "accent" : "light"}
                background="inverse"
                onClick={(event) => {
                  const resolvedSource = resolveSourceForActions(sources[selectedSourceIndex]);
                  handleCopyToMediaShare(resolvedSource, selectedSourceIndex, () => {
                    setCopiedToMediaShareIndex(selectedSourceIndex);
                    setTimeout(() => setCopiedToMediaShareIndex(null), 1500);
                  });
                  recordStat(
                    'CopyToMediaShareButtonClicked',
                    event,
                    statsContext                  );
                }}
                onTouchEnd={() => {
                  const resolvedSource = resolveSourceForActions(sources[selectedSourceIndex]);
                  handleCopyToMediaShare(resolvedSource, selectedSourceIndex, () => {
                    setCopiedToMediaShareIndex(selectedSourceIndex);
                    setTimeout(() => setCopiedToMediaShareIndex(null), 1500);
                  });
                }}
                aria-label={copiedToMediaShareIndex === selectedSourceIndex ? t('sources.added') : t('sources.addToFavorites')}
              >
                {copiedToMediaShareIndex === selectedSourceIndex ? (
                  <CheckIcon />
                ) : (
                  <BookmarksSimpleIcon weight="duotone" />
                )}
              </Button>
            </TpTooltip>
          )}
          {addToTimeline && (
          <TpTooltip
            content={
              isCreateEventFromContainerSource
                ? t('sources.createFromContainer')
                : t('sources.createEvent')
            }
            placement="bottom"
            delayDuration={0}
            sideOffset={2}
          >
            <span className="inline-flex">
            <Button
              iconOnly
              size="sm"
              style={createdEventFromSource ? "solid" : "text"}
              color={createdEventFromSource ? "accent" : "light"}
              background="inverse"
              disabled={isCreateEventFromContainerSource}
              aria-label={
                isCreateEventFromContainerSource
                  ? t('sources.createFromContainer')
                  : createdEventFromSource
                    ? t('sources.eventCreatedFrom')
                    : t('sources.createEvent')
              }
              onClick={(event) => {
                if (isCreateEventFromContainerSource) return;
                recordStat(
                  'CreateEventFromSourceButtonClicked',
                  event,
                  statsContext                );
                const resolvedSource = resolveSourceForActions(sources[selectedSourceIndex]);
                handleCreateEventFromSource(resolvedSource, epochTimeSecs);
              }}
              onTouchEnd={() => {
                if (isCreateEventFromContainerSource) return;
                const resolvedSource = resolveSourceForActions(sources[selectedSourceIndex]);
                handleCreateEventFromSource(resolvedSource, epochTimeSecs);
              }}
            >
              {createdEventFromSource ?
                (<CheckIcon />) :
                (<ListPlusIcon data-testid={`event-from-source-trigger-icon-${answerIndex}-${selectedSourceIndex}`} />)
              }
            </Button>
            </span>
          </TpTooltip>
          )}
          {sourceTextEnabled && (() => {
            const source = sources[selectedSourceIndex];
            // Evidence-locker citations carry a lookup path; the card's loaded
            // file info has the canonical s3:// URL the inspector resolves.
            const inspectable = source && !source.fileInfo && !loadingFileSummary && fileInfo
              ? { ...source, fileInfo }
              : source;
            const eyeButtonProps = {
              iconOnly: true,
              size: 'sm',
              style: 'text',
              color: 'light',
              background: 'inverse',
              'aria-label': 'What TimePilot sees',
              'data-testid': 'inspect-source',
            } as const;
            if (inspectable && hasHierarchyMenu(hierarchy, loadingFileSummary)) {
              // Provenance hierarchy only. Download may also list a PDF preview of a markdown extract.
              return (
                <HierarchyActionMenu
                  hierarchy={hierarchy}
                  title={t('sources.inspectFile')}
                  ariaLabel={t('sources.inspectHierarchy')}
                  onSelect={({ item, label, isContainer, isLast }) =>
                    setInspected({
                      source: inspectable,
                      target: {
                        name: label,
                        container: isContainer,
                        uncited: !isContainer && !isLast,
                        request: inspectRequestForHierarchyFile(
                          inspectable,
                          item.s3Url,
                          isContainer ? 'container' : isLast ? 'cited' : 'extracted',
                        ),
                      },
                    })
                  }
                >
                  <Button {...eyeButtonProps}>
                    <FileMagnifyingGlassIcon />
                  </Button>
                </HierarchyActionMenu>
              );
            }
            return (
              <TpTooltip
                content="What TimePilot sees"
                placement="bottom"
                delayDuration={0}
                sideOffset={2}
              >
                <Button
                  {...eyeButtonProps}
                  onClick={() => setInspected(inspectable ? { source: inspectable, target: null } : null)}
                >
                  <FileMagnifyingGlassIcon />
                </Button>
              </TpTooltip>
            );
          })()}
          {sourceTextEnabled && inspected && (
            <SourceInspectorModal
              source={inspected.source}
              target={inspected.target}
              media={inspected.target?.container || inspected.target?.uncited ? null : sourceCardMediaItem}
              caseId={caseId}
              isOpen
              onOpenChange={(open) => {
                if (!open) setInspected(null);
              }}
            />
          )}
        </div>
      </CardHeader>
      <CardBody
        className={
          fillPreviewContainer
            ? 'flex min-h-0 min-w-0 w-full flex-1 flex-col gap-2'
            // Avoid overflow-x-auto here: it makes overflow-y compute to auto and
            // steals scroll from the answer-item SourceCard scrollport.
            : 'flex h-auto min-w-0 w-full max-w-full shrink-0 flex-col gap-2 overflow-visible'
        }
        style={fillPreviewContainer ? undefined : { height: 'auto', overflow: 'visible', flex: '0 0 auto' }}
      >
        {showImageDescription && (
          <div
            data-testid="source-card-image-description"
            className='mb-1 pb-2 border-b-[1px] border-white/10 border-solid'
          >
            <div className='text-white/30 text-[10px] font-semibold tracking-widest uppercase mb-1.5'>
              {t('sources.description')}
            </div>
            <p className='text-white/80 text-sm leading-relaxed'>
              {imageDescription?.trim()
                ? imageDescription
                : contentModerationImageDescriptionPending
                  ? t('sources.loadingDescription')
                  : t('sources.noDescription')}
            </p>
          </div>
        )}
        {!loadingFileSummary && isMetaFile(sourceCardMediaItem) && (
          <MetaSocialViewerButton
            src={
              sourceCardMediaItem.srcToUse ||
              sourceCardMediaItem.mediaUrl ||
              ''
            }
            mediaItem={sourceCardMediaItem}
            caseId={caseId}
          />
        )}
        <div className={`flex flex-row items-start justify-start gap-2 w-full`}>
          {hasCitationSnippet && (
            <Button
              type="button"
              style="text"
              color="light"
              background="inverse"
              size="sm"
              iconOnly={isCitationExpanded}
              leadingIcon={
                isCitationExpanded ? (
                  <CaretDoubleUpIcon />
                ) : (
                  <CaretDoubleDownIcon />
                )
              }
              aria-expanded={isCitationExpanded}
              aria-label={isCitationExpanded ? t('sources.hideDetails') : t('sources.viewDetails')}
              className="shrink-0"
              onClick={() => {
                setIsCitationExpanded?.(!isCitationExpanded);
              }}
            >
              {isCitationExpanded ? undefined : t('sources.viewDetailsLabel')}
            </Button>
          )}
          {(hasCitationPage || hasCitationSnippet) && (
            <div className={`min-w-0 flex-1 box-border flex flex-col gap-0 ${hasCitationSnippet && !isCitationExpanded ? 'h-[21px] max-h-[21px] overflow-hidden mb-1 opacity-0 transition-opacity duration-300 ease-in-out' : ''}`}>
              {hasCitationPage && (
                <div className='text-white font-normal text-base mb-2'>
                  {sourceCardMediaItem.mediaType === 'pptx' ? t('sources.slide') : t('sources.pageWord')} {selectedSource.page}
                </div>)
              }
              {hasCitationSnippet && (
                <div className="min-w-0 flex-1 overflow-x-auto custom-scrollbar2 flex flex-col gap-2">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={markdownRehypeSanitizePlugins}
                    components={{
                      p: ({ node, children }) => <span className='mb-2 leading-normal'>{children}</span>,
                      h2: ({ node, children }) => <span className='mb-2 leading-normal'>{children}</span>,
                      li: ({ node, children }) => <li className='mb-2 leading-normal'>{children}</li>,
                      a: ({ node, children }) => <a className='text-brand-blue-light italic'>{children}</a>,
                      table: ({ children }) => (
                        <div className="my-2 w-full max-w-full overflow-x-auto custom-scrollbar2 pr-1">
                          <table className="w-max min-w-full border-collapse text-sm text-white">
                            {children}
                            </table>
                        </div>
                      ),
                      thead: ({ children }) => (
                        <thead className="bg-white/10">{children}</thead>
                      ),
                      th: ({ children }) => (
                        <th className="border border-white/20 px-3 py-2 text-left font-semibold leading-snug whitespace-nowrap">
                          {children}
                          </th>
                      ),
                      tr: ({ children }) => (
                        <tr className="even:bg-white/5">{children}</tr>
                      ),
                      td: ({ children }) => (
                        <td className="border border-white/20 px-3 py-2 align-top leading-relaxed break-words min-w-[4rem]">
                          {children}
                          </td>
                      ),
                      pre: ({ children }) => (
                        <pre className="my-2 overflow-x-auto whitespace-pre rounded-sm border border-white/20 bg-black/20 p-3 text-sm leading-relaxed">
                          {children}
                          </pre>
                      ),
                    }}
                  >
                    {visibleCitation?.replace('```json ', '')}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          )}
        </div>
        {loadingFileSummary && !isFullscreen ? (
          <div className='text-white font-normal text-base mb-2 flex flex-col justify-center items-center gap-4 h-full'>
            <Spinner size='lg' color='white' className="opacity-70"/>
            <div className='text-white font-normal text-base mb-2'>{t('sources.loadingInfo')}</div>
          </div>
        ) : previewFallback === 'file_info_failed' ? (
          <div
            data-testid="source-card-file-info-error"
            className="box-border flex min-h-[200px] flex-col items-center justify-center gap-2 rounded-sm border border-dashed border-white/20 bg-black/20 px-6 py-8 text-center text-sm text-white/80"
          >
            {t('sources.loadError')}
          </div>
        ) : citationHasNoViewableMedia ? null : wrapPreviewMedia(renderSourceCardMedia())}
        {!!visibleAttachments && visibleAttachments.length > 0 && (
          <EmailAttachmentsList
            attachments={visibleAttachments}
            caseId={caseId}
            jsonMediaDisplayEnabled={jsonMediaDisplayEnabled}
            defaultOpenIndex={openAttachmentIndex}
            citedAnalysisFilePath={
              citedEmlAttachmentPath ||
              selectedSource?.analysisFilePath ||
              (selectedSource?.sourceContext?.attachmentUrl as string | undefined)
            }
            citedSourceContext={selectedSource?.sourceContext}
            onCopyPath={(attachment, index) => {
              handleCopyAttachmentSourceForTimeline(attachment, index);
              recordStat('CopyAttachmentSourcePathButtonClicked', undefined, statsContext);
            }}
            onAddToFavorites={
              handleCopyToMediaShare
                ? (attachment, index) => {
                    const selectedFetchArgs = resolveEmailFetchArgs(selectedSource);
                    const parentEml =
                      selectedFetchArgs?.kind === 'eml' ? selectedFetchArgs.emlUrl : undefined;
                    const sourceFromAttachment = {
                      sourceFilePath: attachment.attachmentUrl,
                      sourceUrl: attachment.attachmentUrl,
                      originalUrl: selectedSource?.originalUrl || selectedSource?.sourceFilePath,
                      analysisFilePath: attachment.attachmentUrl,
                      dispatchedFilePath: attachment.attachmentUrl,
                      sourceContext: {
                        ...selectedSource?.sourceContext,
                        attachmentUrl: attachment.attachmentUrl,
                        originalUrl: selectedSource?.originalUrl || selectedSource?.sourceFilePath,
                        dispatchedFilePath: parentEml,
                      },
                    } as Source;
                    handleCopyToMediaShare(sourceFromAttachment, -1 - index, () => {
                      setCopiedToMediaShareAttachmentIndex(index);
                      setTimeout(() => setCopiedToMediaShareAttachmentIndex(null), 1500);
                    });
                    recordStat('CopyAttachmentToMediaShareButtonClicked', undefined, statsContext);
                  }
                : undefined
            }
            onCreateEvent={
              addToTimeline
                ? (attachment, index) => {
                    recordStat('CreateEventFromAttachmentButtonClicked', undefined, statsContext);
                    handleCreateEventFromAttachment(sources[selectedSourceIndex], attachment, index);
                  }
                : undefined
            }
            copiedPathIndex={copiedAttachementIndex}
            addedToFavoritesIndex={copiedToMediaShareAttachmentIndex}
            createdEventIndex={createdEventFromAttachment}
          />
        )}
      </CardBody>
    </Card>
  );
};
